import { apiClient } from "./api-client";
import type { Language, CreateLanguageInput, UpdateLanguageInput } from '@/lib/types/language';

type RawLanguage = Partial<Language> & {
  _id?: string;
  createdBy?: Language['createdBy'] | string;
  owner?: Language['createdBy'] | string;
  admin?: Language['createdBy'] | string;
  ownerId?: string;
  ownerName?: string;
  createdByName?: string;
  adminId?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | string;
  approvedBy?: string;
  approvedAt?: string | Date;
};

const toOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const normalizeOwnerRef = (value: unknown): Language['createdBy'] | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const id = toOptionalString(record.id) ?? toOptionalString(record._id);
  const username = toOptionalString(record.username);
  const email = toOptionalString(record.email);
  const name = toOptionalString(record.name);

  if (!id && !username && !email && !name) {
    return undefined;
  }

  return {
    id,
    username,
    email,
    name,
  };
};

const normalizeLanguage = (payload: RawLanguage): Language => {
  const createdBy =
    normalizeOwnerRef(payload.createdBy) ??
    normalizeOwnerRef(payload.owner) ??
    normalizeOwnerRef(payload.admin);
  const createdById =
    toOptionalString(payload.createdById) ??
    createdBy?.id;
  const ownerId =
    toOptionalString(payload.ownerId) ??
    toOptionalString(typeof payload.owner === 'string' ? payload.owner : undefined);
  const adminId =
    toOptionalString(payload.adminId) ??
    toOptionalString(typeof payload.admin === 'string' ? payload.admin : undefined);
  const ownerName =
    toOptionalString(payload.createdByName) ??
    toOptionalString(payload.ownerName) ??
    createdBy?.name ??
    createdBy?.username ??
    createdBy?.email;
  const approvalStatus =
    payload.approvalStatus === 'pending' ||
    payload.approvalStatus === 'approved' ||
    payload.approvalStatus === 'rejected'
      ? payload.approvalStatus
      : undefined;
  const approvedAtValue = payload.approvedAt as unknown;

  return {
    id: String(payload.id ?? payload._id ?? ''),
    code: String(payload.code ?? '').trim(),
    name: String(payload.name ?? '').trim(),
    displayName: String(payload.displayName ?? payload.name ?? '').trim(),
    flag: String(payload.flag ?? '').trim(),
    active: typeof payload.active === 'boolean' ? payload.active : true,
    approvalStatus,
    approvedBy: toOptionalString(payload.approvedBy),
    approvedAt:
      approvedAtValue instanceof Date
        ? approvedAtValue.toISOString()
        : toOptionalString(approvedAtValue),
    createdById,
    ownerId,
    adminId,
    ownerName,
    createdBy,
    createdAt: payload.createdAt,
    updatedAt: payload.updatedAt,
  };
};

const normalizeLanguageCollection = (payload: unknown): Language[] => {
  const rawList = Array.isArray(payload)
    ? payload
    : payload && typeof payload === 'object' && Array.isArray((payload as Record<string, unknown>).data)
      ? (payload as Record<string, unknown>).data as unknown[]
      : [];

  return rawList
    .filter((entry): entry is RawLanguage => Boolean(entry && typeof entry === 'object'))
    .map((entry) => normalizeLanguage(entry));
};

const normalizeSingleLanguage = (payload: unknown): Language => {
  const raw = payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)
    ? ((payload as Record<string, unknown>).data as RawLanguage | undefined)
    : (payload as RawLanguage | undefined);

  return normalizeLanguage(raw ?? {});
};

const sanitizeLanguagePayload = <T extends Record<string, unknown>>(payload: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string') {
        if (key === 'flag') return true;
        return value.trim().length > 0;
      }
      return true;
    })
  ) as Partial<T>;
};

export const languageApi = {
  /**
   * Get all languages
   */
  getLanguages: async (): Promise<Language[]> => {
    const response = await apiClient.get('/langues');
    return normalizeLanguageCollection(response.data);
  },

  getMyLanguages: async (): Promise<Language[]> => {
    const response = await apiClient.get('/langues/admin/mine');
    return normalizeLanguageCollection(response.data);
  },

  getLanguage: async (id: string): Promise<Language> => {
    const response = await apiClient.get(`/langues/${id}`);
    return normalizeSingleLanguage(response.data);
  },

  createLanguage: async (data: CreateLanguageInput): Promise<Language> => {
    const response = await apiClient.post(
      '/langues',
      sanitizeLanguagePayload(data as unknown as Record<string, unknown>),
    );
    return normalizeSingleLanguage(response.data);
  },

  updateLanguage: async (id: string, data: UpdateLanguageInput): Promise<Language> => {
    const response = await apiClient.patch(
      `/langues/${id}`,
      sanitizeLanguagePayload(data as Record<string, unknown>),
    );
    return normalizeSingleLanguage(response.data);
  },

  deleteLanguage: async (id: string): Promise<void> => {
    await apiClient.delete(`/langues/${id}`);
  },

  getActiveLanguages: async (): Promise<Language[]> => {
    const response = await apiClient.get('/langues', {
      params: { active: true },
    });
    return normalizeLanguageCollection(response.data);
  },

  getPendingApprovalLanguages: async (): Promise<Language[]> => {
    const response = await apiClient.get('/langues/pending/approval');
    return normalizeLanguageCollection(response.data);
  },

  updateLanguageApproval: async (
    id: string,
    approvalStatus: 'approved' | 'rejected'
  ): Promise<Language> => {
    const endpoint = approvalStatus === 'approved' ? 'approve' : 'reject';
    const response = await apiClient.patch(
      `/langues/${id}/${endpoint}`,
      { approvalStatus },
    );
    return normalizeSingleLanguage(response.data);
  },
};
